import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/opt/build/repo/src/components/Layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`Los plugins de Gatsby son paquetes de Node.js que implementan APIs propias de
Gatsby y, si nuestro proyecto es grande, nos permiten modularizar algunas
personalizaciones que tengamos y convertirlas en plugins específicos para lo que
necesitemos.`}</p>
    <p>{`Gatsby está diseñado para ser extensible, y los plugins nos sirven para eso:
extender y modificar todo lo que Gatsby hace.`}</p>
    <p>{`Algunas de las cosas que los plugins nos permiten hacer son:`}</p>
    <ul>
      <li parentName="ul">{`Añadir contenido o data externa (ej. CMS, archivos estáticos, una API REST) a
nuestra data de GraphQL`}</li>
      <li parentName="ul">{`Transformar data de otros formatos (ej. Markdown, YAML, CSV) a objetos JSON`}</li>
      <li parentName="ul">{`Añadir servicios externos (ej. Google Analytics, Instagram) a nuestro proyecto`}</li>
      <li parentName="ul">{`Muchísimas cosas más!`}</li>
    </ul>
    <p>{`En `}<a parentName="p" {...{
        "href": "https://www.gatsbyjs.org/plugins/"
      }}>{`este link`}</a>{` podemos ver todos los plugins
que nos ofrece el ecosistema de Gatsby 😀`}</p>
    <h2>{`Tipos de plugins`}</h2>
    <p>{`Gatsby nos propone, entre otros, dos tipos importantes (a mi parecer) de plugins
para hacer algunas de las cosas que recién nombramos:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.gatsbyjs.org/tutorial/part-five/"
        }}><code parentName="a" {...{
            "className": "language-text"
          }}>{`source plugins`}</code></a>{`: Los plugins
de tipo `}<code parentName="li" {...{
          "className": "language-text"
        }}>{`source`}</code>{` (fuente) nos sirven para crear lo que sería una "fuente de
datos" para que nosotros podamos, mediante GraphQL, consultar y utilizar esa
data en el frontend. (La data consultada puede ser data de un CMS, imágenes,
Markdown, otros archivos estáticos locales, etc.)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.gatsbyjs.org/tutorial/part-six/"
        }}><code parentName="a" {...{
            "className": "language-text"
          }}>{`transformer plugins`}</code></a>{`: Este
tipo de plugins nos permite `}<em parentName="li">{`convertir o transformar`}</em>{` (de ahí el nombre 😝) la
data consultada de nuestra fuente de datos, por ejemplo, nos permite convertir
Markdown a HTML, procesar imágenes, archivos PDF, etc.`}</li>
    </ul>
    <p>{`Veamos un poco cómo se usan estos tipos de plugins en nuestro proyecto.`}</p>
    <h2>{`Usando plugins en nuestro proyecto`}</h2>
    <p>{`Los plugins de Gatsby, al ser paquetes de Node.js, podemos instalarlos como
cualquier otro paquete utilizando NPM, Yarn, o lo que más les guste. Analicemos
nuestro proyecto para ver qué plugins usamos, veamos el archivo
`}<code parentName="p" {...{
        "className": "language-text"
      }}>{`gatsby-config.js`}</code>{` que es donde indicamos qué plugins vamos a usar, deberíamos
ver algo así:`}</p>
    <div {...{
      "className": "gatsby-highlight",
      "data-language": "javascript"
    }}><pre parentName="div" {...{
        "className": "language-javascript"
      }}><code parentName="pre" {...{
          "className": "language-javascript"
        }}>{`module`}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`.`}</span>{`exports `}<span parentName="code" {...{
            "className": "token operator"
          }}>{`=`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
  siteMetadata`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`:`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
    title`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`:`}</span>{` `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'Gatsby Default Starter'`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
  `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
  plugins`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`:`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`[`}</span>{`
    `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'gatsby-plugin-react-helmet'`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
    `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
      resolve`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`:`}</span>{` `}<span parentName="code" {...{
            "className": "token template-string"
          }}><span parentName="span" {...{
              "className": "token template-punctuation string"
            }}>{`\``}</span><span parentName="span" {...{
              "className": "token string"
            }}>{`gatsby-source-filesystem`}</span><span parentName="span" {...{
              "className": "token template-punctuation string"
            }}>{`\``}</span></span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
      options`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`:`}</span>{` `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`{`}</span>{`
        name`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`:`}</span>{` `}<span parentName="code" {...{
            "className": "token template-string"
          }}><span parentName="span" {...{
              "className": "token template-punctuation string"
            }}>{`\``}</span><span parentName="span" {...{
              "className": "token string"
            }}>{`images`}</span><span parentName="span" {...{
              "className": "token template-punctuation string"
            }}>{`\``}</span></span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
        path`}<span parentName="code" {...{
            "className": "token operator"
          }}>{`:`}</span>{` `}<span parentName="code" {...{
            "className": "token template-string"
          }}><span parentName="span" {...{
              "className": "token template-punctuation string"
            }}>{`\``}</span><span parentName="span" {...{
              "className": "token interpolation"
            }}><span parentName="span" {...{
                "className": "token interpolation-punctuation punctuation"
              }}>{`\${`}</span>{`__dirname`}<span parentName="span" {...{
                "className": "token interpolation-punctuation punctuation"
              }}>{`}`}</span></span><span parentName="span" {...{
              "className": "token string"
            }}>{`/src/images`}</span><span parentName="span" {...{
              "className": "token template-punctuation string"
            }}>{`\``}</span></span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
      `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
    `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`}`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
    `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'gatsby-transformer-sharp'`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
    `}<span parentName="code" {...{
            "className": "token string"
          }}>{`'gatsby-plugin-sharp'`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`,`}</span>{`
    `}<span parentName="code" {...{
            "className": "token punctuation"
          }}>{`[`}</span><span parentName="code" {...{
            "className": "token operator"
          }}>{`...`}</span><span parentName="code" {...{
            "className": "token punctuation"
          }}>{`]`}</span></code></pre></div>
    <p>{`Con lo cual podemos observar que ya estamos indicando que vamos a utilizar,
entre otros, los plugins:`}</p>
    <ul>
      <li parentName="ul">{`gatsby-`}<strong parentName="li">{`source`}</strong>{`-filesystem`}</li>
      <li parentName="ul">{`gatsby-`}<strong parentName="li">{`transformer`}</strong>{`-sharp`}</li>
      <li parentName="ul">{`gatsby-plugin-sharp`}</li>
    </ul>
    <p>{`Y vemos que la forma de importarlos (verificando antes que estén instalados y
estén en nuestro `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`package.json`}</code>{`) es agregando un string con el nombre del plugin
en el array de plugins de nuestro archivo de configuración. Pero por qué
`}<code parentName="p" {...{
        "className": "language-text"
      }}>{`gatsby-source-filesystem`}</code>{` está como objeto? Eso es porque algunos plugins
reciben parámetros para que funcionen como necesitemos, suele ser por medio del
objeto `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`options`}</code>{` y nosotros debemos indicar el nombre en un string en la clave
`}<code parentName="p" {...{
        "className": "language-text"
      }}>{`resolve`}</code>{` del objeto. La utilización de cada plugin suele explicarse en la
documentación de cada plugin, así que a no tener miedo y leer que van a
encontrar mejores explicaciones que la mía jajaja. Bueno, hablando de eso, vamos
a ver la
`}<a parentName="p" {...{
        "href": "https://www.gatsbyjs.org/packages/gatsby-transformer-sharp/"
      }}>{`documentación de gatsby-transformer-sharp`}</a>{`
donde vamos a ver que nos indica que es un `}<strong parentName="p">{`plugin oficial`}</strong>{` de Gatsby y
podemos ver que gatsby-plugin-sharp es un adaptador de una biblioteca `}<em parentName="p">{`Sharp`}</em>{`
que existe y se encarga de manejar imágenes. Entonces, una idea de cómo sería el
flujo desde consultar una imagen que tenemos en una carpeta local hasta que se
usa en el frontend, sería algo como lo siguiente:`}</p>
    <p><strong parentName="p">{`Se genera una fuente de datos desde nuestra carpeta con imágenes`}</strong>{` ⇒ `}<strong parentName="p">{`Se
consultan esos datos que tiene nuestras imágenes desde el frontend`}</strong>{` ⇒ `}<strong parentName="p">{`Se usan
las imágenes en el frontend con el componente `}<code parentName="strong" {...{
          "className": "language-text"
        }}>{`gatsby-image`}</code></strong>{` (siendo
procesadas con `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`gatsby-transformer-sharp`}</code>{` y `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`gatsby-plugin-sharp`}</code>{`, que ya viene
todo preparado para nosotros 🥰)`}</p>
    <p>{`Para varias cosas nos vamos a topar con esta dinámica de Crear fuente de datos ⇒
Procesar datos ⇒ Usarlos, y si traducimos, estaríamos teniendo que hacer
`}<strong parentName="p">{`Source`}</strong>{` ⇒ `}<strong parentName="p">{`Transformer`}</strong>{` ⇒ `}<strong parentName="p">{`Use`}</strong></p>
    <p>{`No toda la data tiene que pasar obligatoriamente por un plugin de tipo
Transformer, eso vamos a ir viendo mientras usemos más plugins.`}</p>
    <p>{`Hasta acá creo que estamos bien para continuar con el siguiente tema, que es
nuestra forma de consultar la fuente de datos que creemos con nuestros plugins
de tipo `}<strong parentName="p">{`Source`}</strong>{`, sigamos con `}<code parentName="p" {...{
        "className": "language-text"
      }}>{`GraphQL`}</code>{` 😉`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      